import type { DeviceMediaItem } from "@workspace/api-client-react";
import {
  resolveConflict,
  type ConflictPolicy,
  type QueueEntry,
} from "@workspace/player-core";
import {
  shouldPrependTone,
  toneQueueEntry,
  type AnnouncementToneSetting,
} from "./announcement-tone.ts";

/**
 * Pure decision core of a timed audio fire (announcement/ad interrupt
 * orchestration, CB-112) — extracted verbatim from
 * `use-playback.tsx#applyTimedEventFire`'s `plan.audio === "play"` branch so
 * the cut/defer/skip decision, the audio-item filter, and the one-tone-per-
 * takeover prepend rule are testable without the hook. The hook keeps every
 * side effect (queue mutation, playEntry, debug metrics, bed pause).
 *
 * Semantics pinned here:
 *  - `resolveConflict(conflictPolicy, channelAlreadyPlaying)` decides the
 *    shape: `skip` drops the fire entirely, `wait` defers it (queue-front, no
 *    cut-in — the on-air item finishes first), `play` cuts in now.
 *  - When the fire's items mix types, only the audio items play on the audio
 *    bus; an all-non-audio fire falls back to the full item list.
 *  - A one-shot announcement fire gets the attention tone once at the head of
 *    the takeover — never when the on-air item or the queue head is already a
 *    tone (`shouldPrependTone`), never on other channels, and never when the
 *    tone has no playable URL.
 */

export interface TimedAudioFireInput {
  /** The fire's channel (only "announcements" ever gets the tone). */
  channelType: string;
  /** Per-slot conflict policy (readSlotBehavior(slot).conflictPolicy). */
  conflictPolicy: ConflictPolicy;
  /** Is this channel already audibly playing right now? */
  channelAlreadyPlaying: boolean;
  /** Resolved playable items for the fire (non-empty; hook returns earlier otherwise). */
  items: DeviceMediaItem[];
  /** Entry builder — the hook binds makeEntry(...) with slot/playlist/extras. */
  makeEntry: (item: DeviceMediaItem) => QueueEntry;
  /** Configured attention tone, or null when off. */
  tone: AnnouncementToneSetting | null;
  /** Playable tone URL, or null when it cannot play (unhydrated asset tone). */
  toneSrc: string | null;
  /** Item id currently on air (tone dedupe). */
  currentItemId: string | null | undefined;
  /** Item id at the head of the interrupt queue (tone dedupe). */
  queueHeadItemId: string | null | undefined;
}

export interface TimedAudioFirePlan {
  /** skip = drop the fire; defer = queue without cutting in; cut = play now. */
  kind: "skip" | "defer" | "cut";
  /** Ordered entries for the interrupt queue (tone first when prepended). */
  entries: QueueEntry[];
}

export function planTimedAudioFire(
  input: TimedAudioFireInput,
): TimedAudioFirePlan {
  const resolution = resolveConflict(
    input.conflictPolicy,
    input.channelAlreadyPlaying,
  );
  if (resolution === "skip") return { kind: "skip", entries: [] };

  const audioItems = input.items.filter((i) => i.type === "audio");
  const entries = (audioItems.length > 0 ? audioItems : input.items).map(
    input.makeEntry,
  );

  if (
    input.channelType === "announcements" &&
    entries.length > 0 &&
    input.tone &&
    input.toneSrc &&
    shouldPrependTone(input.tone, input.currentItemId, input.queueHeadItemId)
  ) {
    entries.unshift(toneQueueEntry(input.tone, input.toneSrc));
  }

  return { kind: resolution === "wait" ? "defer" : "cut", entries };
}
